import { useState, useEffect, useRef, useMemo } from "react";
import { Fade, Box, Tooltip, Button } from "@mui/material";
import useLanguage from "../../../hooks/useLanguage";
import {
  CELL_WIDTH, ROW_HEIGHT, HOURS,
  GuideRoot, CornerBox,
  HeaderScroller, HeaderInner, HourCell, HalfHourTick,
  DayScroller, DayInner, DayRowBox,
  MainScroller, GridContent, GridDayRow, HourLine,
  ShowBlock, ShowInner,
  TimeNeedle, TimeNeedleDot,
  PopoverPaper, StyledPopper,
  HourLabel, DayAbbr, DayDate,
  ShowName, ShowTime,
  PopoverTitle, PopoverDesc, PopoverTime,
} from "./TVGuide.style";

// Card fill IS the recording status — color carries meaning, not decoration.
const STATUS_COLOR = {
  scheduled: "#5B8DEF", // blue
  planned: "#5B8DEF", // blue — resolved & will record
  recording: "#F5A524", // amber
  captured: "#2FB686", // green (recorded)
  failed: "#E5484D", // red
  recurring: "#B06EEE", // purple
};
// Unscheduled shows: a single muted outline (no per-show cycle).
const NEUTRAL = "#9aa0a8";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(utc) {
  const d = new Date(utc);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function toDateKey(utc) {
  const d = new Date(utc);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseDayLabel(dateKey, dayAbbr) {
  const [y, m, day] = dateKey.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  return { abbr: dayAbbr[d.getDay()], date: day };
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * TVGuide
 * @param {string|number} width     - CSS width  (e.g. "100%", 800)
 * @param {string|number} height    - CSS height (e.g. 400, "60vh")
 * @param {{ name: string, description: string, startTime: number, endTime: number }[]} shows
 * @param {object[]} selected        - Stored RecordingOrders (oneshot | recurring)
 * @param {Function} onShowClick     - Called with the clicked show object
 */
export default function TVGuide({
  width = "100%",
  height = 400,
  shows = [],
  selected = [],
  plan = [],
  unresolved = [],
  onShowClick,
  onSelectionRecord,
}) {
  const [{ pages: { stream: text } }] = useLanguage();

  // ── Refs for scroll sync ─────────────────────────────────────────────────────
  const mainRef    = useRef(null);
  const headerRef  = useRef(null);
  const dayColRef  = useRef(null);
  const closeTimer = useRef(null);

  // ── Derived data (must precede effects that reference these values) ──────────
  const today       = new Date();
  const todayKey    = toDateKey(today);
  const currentHour = today.getHours() + today.getMinutes() / 60;

  // ── Scroll to current time on mount ─────────────────────────────────────────
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollLeft = Math.max(0, (currentHour - 1) * CELL_WIDTH);
    }
  }, []);

  // ── Scroll sync + limit ──────────────────────────────────────────────────────
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onScroll = () => {
      // Clamp so the user cannot scroll past the last hour column
      const maxLeft    = Math.max(0, CELL_WIDTH * 24 - main.clientWidth);
      const safeLeft   = Math.min(main.scrollLeft, maxLeft);
      if (headerRef.current) headerRef.current.scrollLeft = safeLeft;
      if (dayColRef.current) dayColRef.current.scrollTop  = main.scrollTop;
      setPopper((p) => (p.open ? { ...p, open: false } : p));
    };
    main.addEventListener("scroll", onScroll, { passive: true });
    return () => main.removeEventListener("scroll", onScroll);
  }, []);

  // ── Always 7 days starting from today ───────────────────────────────────────
  const days = useMemo(() => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return toDateKey(d);
    });
  }, []);

  const showsByDay = useMemo(() => {
    const map = new Map();
    shows.forEach((show, i) => {
      const key = toDateKey(show.startTime);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ show, index: i });
    });
    return map;
  }, [shows]);

  // ── Hover popover ────────────────────────────────────────────────────────────
  const [popper, setPopper] = useState({ open: false, anchorEl: null, show: null });

  const handleEnter = (e, show) => {
    clearTimeout(closeTimer.current);
    setPopper({ open: true, anchorEl: e.currentTarget, show });
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(
      () => setPopper({ open: false, anchorEl: null, show: null }),
      80,
    );
  };

  // ── Selection ────────────────────────────────────────────────────────────────
  // A show lights up if a resolved PLAN window COVERS it — i.e. the airing overlaps
  // the recording window's time span. The old check keyed on an exact
  // `segment.showStartUtcMs === show.startTime` ms match, which silently failed for
  // (a) coalesced windows, whose segment boundaries are synthetic splits that match
  // no real airing, and (b) any guide drift after the resolve (a show nudged by even
  // a minute). Both record fine but weren't lit. Overlap follows the actual coverage.
  // Status (planned/recording/captured/failed) drives color.
  const orderFor = (show) => {
    const s = new Date(show.startTime).getTime();
    const e = new Date(show.endTime).getTime();
    const p = plan.find((w) => s < w.endUtcMs && e > w.startUtcMs);
    return p ? { kind: "plan", status: p.status } : null;
  };

  // Multi-select a CONTIGUOUS run of shows in one day: plain click opens the single
  // dialog (and sets the anchor); shift-click extends the range from the anchor. The
  // selected shows are recorded as one continuous recording (the server coalesces them).
  const [sel, setSel] = useState({ dateKey: null, anchorPos: null, shows: [] });
  const clearSel = () => setSel({ dateKey: null, anchorPos: null, shows: [] });

  const handleClick = (e, show, dateKey, pos, dayShows) => {
    if (e.shiftKey && sel.dateKey === dateKey && sel.anchorPos != null) {
      const lo = Math.min(sel.anchorPos, pos);
      const hi = Math.max(sel.anchorPos, pos);
      setSel({ dateKey, anchorPos: sel.anchorPos, shows: dayShows.slice(lo, hi + 1).map((x) => x.show) });
      return;
    }
    setSel({ dateKey, anchorPos: pos, shows: [show] });
    onShowClick?.(show);
  };

  const todayRow = days.indexOf(todayKey);

  return (
    <GuideRoot $width={width} $height={height}>

      {/* ── Top-left corner ── */}
      <CornerBox />

      {/* ── Hour header ── */}
      <HeaderScroller ref={headerRef}>
        <HeaderInner>
          {HOURS.map((h) => (
            <HourCell key={h}>
              <HourLabel
                variant="caption"
                fontWeight={600}
                $active={Math.floor(currentHour) === h && days.includes(todayKey)}
              >
                {String(h).padStart(2, "0")}:00
              </HourLabel>
              <HalfHourTick />
            </HourCell>
          ))}
        </HeaderInner>
      </HeaderScroller>

      {/* ── Day column ── */}
      <DayScroller ref={dayColRef}>
        <DayInner>
          {days.map((dateKey) => {
            const { abbr, date } = parseDayLabel(dateKey, text.days.abbr);
            const isToday = dateKey === todayKey;
            return (
              <DayRowBox key={dateKey} $isToday={isToday}>
                <DayAbbr variant="caption" fontWeight={700} $isToday={isToday}>
                  {abbr}
                </DayAbbr>
                <DayDate variant="caption" $isToday={isToday}>
                  {isToday ? text.days.today : date}
                </DayDate>
              </DayRowBox>
            );
          })}
        </DayInner>
      </DayScroller>

      {/* ── Main scrollable grid ── */}
      <MainScroller ref={mainRef}>
        <GridContent>

          {/* Day rows */}
          {days.map((dateKey, dayIndex) => {
            const isToday    = dateKey === todayKey;
            const dayShows   = showsByDay.get(dateKey) ?? [];
            const [y, m, d]  = dateKey.split("-").map(Number);
            const dayStartMs = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();

            return (
              <GridDayRow key={dateKey} $isToday={isToday} style={{ top: dayIndex * ROW_HEIGHT }}>

                {/* Hour grid lines */}
                {HOURS.map((h) => (
                  <HourLine key={h} style={{ left: h * CELL_WIDTH }} />
                ))}

                {/* Unresolved recurring markers — a dashed ghost at the slot the
                    recurring expected but the resolver couldn't match that day. */}
                {unresolved
                  .filter((u) => u.date === dateKey)
                  .map((u, ui) => {
                    const ord = selected.find((s) => s.id === u.orderId && s.type === "recurring");
                    if (!ord?.startLocal) return null;
                    const [hh, mm] = ord.startLocal.split(":").map(Number);
                    const startH = hh + mm / 60;
                    if (startH >= 24) return null;
                    const widthH = Math.max((ord.durationMin || 30) / 60, 0.4);
                    return (
                      <Tooltip key={`u-${u.orderId}-${ui}`} arrow title={`לא נמצאה התאמה היום: ${u.title}`}>
                        <Box
                          sx={{
                            position: "absolute",
                            left: startH * CELL_WIDTH + 2,
                            width: widthH * CELL_WIDTH - 4,
                            top: 6,
                            bottom: 6,
                            borderRadius: 1,
                            border: "1.5px dashed #E5A524",
                            background:
                              "repeating-linear-gradient(45deg,transparent,transparent 6px,rgba(229,165,36,.12) 6px,rgba(229,165,36,.12) 12px)",
                            zIndex: 1,
                          }}
                        />
                      </Tooltip>
                    );
                  })}

                {/* Show blocks */}
                {dayShows.map(({ show, index }, pos) => {
                  const startMs  = new Date(show.startTime).getTime();
                  const endMs    = new Date(show.endTime).getTime();
                  const startH   = (startMs - dayStartMs) / 3600000;
                  const endH     = Math.min((endMs - dayStartMs) / 3600000, 24);
                  const duration = endH - startH;
                  if (duration <= 0 || startH >= 24) return null;

                  const order      = orderFor(show);
                  const isSel      = !!order;
                  const blockColor = isSel
                    ? STATUS_COLOR[order.status] || STATUS_COLOR.recurring
                    : NEUTRAL;
                  const inRange = sel.shows.length >= 2 && sel.shows.includes(show);

                  return (
                    <ShowBlock
                      key={index}
                      $color={blockColor}
                      $isSelected={isSel}
                      style={{
                        left:  startH * CELL_WIDTH + 2,
                        width: duration * CELL_WIDTH - 4,
                        ...(inRange ? { outline: "2.5px solid #111118", outlineOffset: "-1px", zIndex: 2 } : {}),
                      }}
                      onClick={(e) => handleClick(e, show, dateKey, pos, dayShows)}
                      onMouseEnter={(e) => handleEnter(e, show)}
                      onMouseLeave={handleLeave}
                    >
                      <ShowInner>
                        <ShowName
                          variant="caption"
                          fontWeight={600}
                          noWrap
                          $selected={isSel}
                          $fontSize={Math.min(11, 9 + duration * 0.4)}
                        >
                          {show.name}
                        </ShowName>
                        {duration > 0.5 && (
                          <ShowTime
                            variant="caption"
                            noWrap
                            $selected={isSel}
                          >
                            {formatTime(show.startTime)} – {formatTime(show.endTime)}
                          </ShowTime>
                        )}
                      </ShowInner>
                    </ShowBlock>
                  );
                })}
              </GridDayRow>
            );
          })}

          {/* Current-time red line (today's row only) */}
          {todayRow >= 0 && (
            <TimeNeedle style={{ left: currentHour * CELL_WIDTH, top: todayRow * ROW_HEIGHT }}>
              <TimeNeedleDot />
            </TimeNeedle>
          )}

        </GridContent>
      </MainScroller>

      {/* ── Multi-select action bar (contiguous run → one recording) ── */}
      {sel.shows.length >= 2 && (
        <Box
          sx={{
            position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
            zIndex: 10, display: "flex", alignItems: "center", gap: 1.5,
            bgcolor: "#111118", color: "#fff", borderRadius: 2, px: 2, py: 1, boxShadow: 4,
          }}
        >
          <Box component="span" sx={{ fontSize: 13 }}>{sel.shows.length} תוכניות נבחרו</Box>
          <Button
            size="small" variant="contained"
            onClick={() => { onSelectionRecord?.(sel.shows); clearSel(); }}
            sx={{ bgcolor: "#9b46e1", "&:hover": { bgcolor: "#7d35b8" } }}
          >
            הקלט כרצף אחד
          </Button>
          <Button size="small" sx={{ color: "rgba(255,255,255,.7)" }} onClick={clearSel}>נקה</Button>
        </Box>
      )}

      {/* ── Hover popover ── */}
      <StyledPopper
        open={popper.open}
        anchorEl={popper.anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={120}>
            <PopoverPaper>
              {popper.show && (
                <>
                  <PopoverTitle variant="body2" fontWeight={600}>
                    {popper.show.name}
                  </PopoverTitle>
                  {popper.show.description && (
                    <PopoverDesc variant="caption" color="text.secondary">
                      {popper.show.description}
                    </PopoverDesc>
                  )}
                  <PopoverTime variant="caption" color="text.disabled">
                    {formatTime(popper.show.startTime)} – {formatTime(popper.show.endTime)}
                  </PopoverTime>
                </>
              )}
            </PopoverPaper>
          </Fade>
        )}
      </StyledPopper>
    </GuideRoot>
  );
}
